// Cheap ground contact for VRM avatars.
//
// FixedRetargetedAvatar keeps feet on the floor by recomputing the skinned
// bounding box of every mesh every frame. VRoid exports split one avatar into
// ~90 primitives, which made that ~12 ms per frame on a desktop (Quest: worse).
//
// Instead, once in bind pose, every skinned vertex is grouped under the
// humanoid bone that mostly carries it (its dominant joint, or that joint's
// nearest humanoid ancestor — hair and skirt joints roll up to head/hips) and
// each group keeps its support points: the most extreme vertex along 26
// directions, in that bone's space. However a bone turns, its lowest vertex is
// (nearly) one of those. Per frame only these few hundred candidates are
// skinned exactly (real blend weights, so creases and soles are right) and the
// lowest one is the avatar's lowest point.
import * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';

const _v = new THREE.Vector3();
const DIRS: THREE.Vector3[] = [];
for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) {
  if (x || y || z) DIRS.push(new THREE.Vector3(x, y, z).normalize());
}
const _inv = new THREE.Matrix4();

export interface VrmGroundProbe {
  /** World Y of the avatar's lowest skin point. */
  lowestY(): number;
}

/** Call while the VRM is still in bind pose (before any animation). */
export function createVrmGroundProbe(vrm: VRM): VrmGroundProbe | null {
  const scene = vrm.scene;
  scene.updateMatrixWorld(true);
  const humanoid = new Set<THREE.Object3D>();
  for (const name of Object.keys((vrm.humanoid as any).humanBones || {})) {
    const b = vrm.humanoid.getRawBoneNode(name as any);
    if (b) humanoid.add(b);
  }
  if (!humanoid.size) return null;
  const owner = new Map<THREE.Object3D, THREE.Object3D | null>();
  const ownerOf = (bone: THREE.Object3D): THREE.Object3D | null => {
    if (owner.has(bone)) return owner.get(bone)!;
    let o: THREE.Object3D | null = bone;
    while (o && !humanoid.has(o)) o = o.parent;
    owner.set(bone, o);
    return o;
  };

  // Per bone: best vertex (and its score) along each direction.
  const support = new Map<THREE.Object3D, { idx: { mesh: THREE.SkinnedMesh; i: number }[]; score: number[] }>();
  const inverses = new Map<THREE.Object3D, THREE.Matrix4>();
  scene.traverse((o: any) => {
    if (!o.isSkinnedMesh) return;
    const mesh = o as THREE.SkinnedMesh;
    mesh.skeleton.update();
    const g = mesh.geometry;
    const si = g.getAttribute('skinIndex'), sw = g.getAttribute('skinWeight'), pos = g.getAttribute('position');
    if (!si || !sw || !pos) return;
    for (let i = 0; i < pos.count; i++) {
      let best = 0, bw = -1;
      for (let k = 0; k < 4; k++) { const w = sw.getComponent(i, k); if (w > bw) { bw = w; best = si.getComponent(i, k); } }
      const joint = mesh.skeleton.bones[best];
      const bone = joint && ownerOf(joint);
      if (!bone) continue;
      let inv = inverses.get(bone);
      if (!inv) { inv = _inv.copy(bone.matrixWorld).invert().clone(); inverses.set(bone, inv); }
      mesh.getVertexPosition(i, _v).applyMatrix4(mesh.matrixWorld).applyMatrix4(inv);
      let sp = support.get(bone);
      if (!sp) { sp = { idx: DIRS.map(() => ({ mesh, i })), score: DIRS.map(() => -Infinity) }; support.set(bone, sp); }
      for (let d = 0; d < DIRS.length; d++) {
        const sc = _v.dot(DIRS[d]);
        if (sc > sp.score[d]) { sp.score[d] = sc; sp.idx[d] = { mesh, i }; }
      }
    }
  });
  if (!support.size) return null;

  // Unique candidates, grouped by mesh.
  const byMesh = new Map<THREE.SkinnedMesh, Set<number>>();
  for (const sp of support.values()) for (const { mesh, i } of sp.idx) {
    if (!byMesh.has(mesh)) byMesh.set(mesh, new Set());
    byMesh.get(mesh)!.add(i);
  }
  const candidates = [...byMesh].map(([mesh, set]) => ({ mesh, idx: Int32Array.from(set) }));
  const skeletons = [...new Set(candidates.map((c) => c.mesh.skeleton))];
  return {
    // Matrices must be current: the caller runs updateMatrixWorld first.
    lowestY() {
      for (const sk of skeletons) sk.update();
      let low = Infinity;
      for (const { mesh, idx } of candidates) {
        const e = mesh.matrixWorld.elements;
        for (let k = 0; k < idx.length; k++) {
          mesh.getVertexPosition(idx[k], _v);
          const y = e[1] * _v.x + e[5] * _v.y + e[9] * _v.z + e[13];
          if (y < low) low = y;
        }
      }
      return low;
    },
  };
}
